import { slopeEstimationPercentile, interceptEstimationPercentile } from './shared-util';
import { dayIso, dayNumber, onePointPerDay } from './body-comp-overview';

/**
 * A running trend line with a 30/70 percentile tunnel, for charts of weight, skinfold or waist
 * readings over ranges longer than the 14 days one straight fit can describe.
 *
 * It is built only from the app's Theil-Sen estimators. For every day there is one straight
 * "trailing fit" over the window ending that day, which is the same fit the app's 14-day trend
 * uses. A day lies inside many of those fits, and its trend value is their weighted average.
 * One fit per day would be spiky; the average is not.
 *
 * Why this method, what else was measured and what the numbers were: design-docs/running-trend.md.
 */

/** Which way a stretch of the tunnel is going. */
export enum TrendDirection {
  Falling = 'falling',
  Rising = 'rising',
  Flat = 'flat',
  Uncertain = 'uncertain',
}

export interface RunningTrendOptions {
  /** How many days each straight fit looks at. */
  windowDays: number;
  /** Fewest readings a fit needs before it is used. */
  minSamples: number;
  /** Tunnel edges that both move less than this per week count as flat (same unit as the readings). */
  flatPerWeek: number;
}

/** Weight: the 14 days the app's own straight trend line uses. */
export const RUNNING_TREND_WEIGHT_OPTIONS: RunningTrendOptions = { windowDays: 14, minSamples: 4, flatPerWeek: 0.1 };

/**
 * Skinfolds and waist: 28 days. They are measured every 3 to 5 days, so a 14-day window often
 * holds fewer than the 3 readings a fit needs and the line would keep breaking.
 */
export const RUNNING_TREND_BODY_FAT_OPTIONS: RunningTrendOptions = { windowDays: 28, minSamples: 3, flatPerWeek: 0.05 };

export interface RunningTrendDay {
  /** The day, in the same numbering as the readings' x. */
  x: number;
  /** The trend line. */
  value: number;
  /** Lower edge of the 30/70 tunnel. */
  lower: number;
  /** Upper edge of the 30/70 tunnel. */
  upper: number;
  /** Median slope per day, averaged the same way as the value. */
  slope: number;
  /** 30th percentile slope per day, averaged the same way. */
  slope30: number;
  /** 70th percentile slope per day, averaged the same way. */
  slope70: number;
  /** Which way the tunnel is going here, read off the tunnel itself. Uncertain when it has no tunnel half a window away to compare with. */
  direction: TrendDirection;
  /**
   * True while a reading taken later could still move this day's value, tunnel edges or slopes.
   * The direction can change a little longer on the few days that had to be compared with a later
   * tunnel, because that later tunnel may itself still be provisional.
   */
  isProvisional: boolean;
}

/**
 * How much a fit still counts for a day at the very edge of its own readings. Not zero, so the
 * newest day, which only its own fit covers and always at that fit's edge, still gets a value.
 */
const EDGE_WEIGHT = 0.05;

interface TrailingFit {
  slope: number;
  intercept: number;
  slope30: number;
  slope70: number;
  /** first and last day this fit has a reading on; it is never used outside them */
  firstX: number;
  lastX: number;
}

/**
 * Compute the running trend for every day from `fromDay` to `toDay`.
 *
 * @param samples Readings as {x: day number, y: value}. Whole days, any origin, any order.
 * @param fromDay First day to return.
 * @param toDay Last day to return. Pass today: it is also the newest day a fit may end on.
 * @throws When `options.windowDays` is below 2 or not a finite number, or when either day is not a finite number.
 * @returns One entry per day, oldest first (none when `toDay` is before `fromDay`). `undefined` where no fit covers the day: before the
 *   first reading, after the last one, inside a gap longer than the window, or where too few
 *   readings exist.
 */
export function computeRunningTrend(
  samples: { x: number; y: number }[],
  fromDay: number,
  toDay: number,
  options: RunningTrendOptions,
): (RunningTrendDay | undefined)[] {
  const windowDays = Math.floor(options.windowDays);
  // a window too short to hold a line is a mistake in the calling code, not a property of the data
  if (!(windowDays >= 2) || !Number.isFinite(windowDays)) {
    throw Error(`computeRunningTrend(): bad windowDays=${options.windowDays}, expected a whole number of 2 or more`);
  }
  // so is a range with no real start or end: the loops below would never finish
  if (!Number.isFinite(fromDay) || !Number.isFinite(toDay)) {
    throw Error(`computeRunningTrend(): bad range ${fromDay}..${toDay}, expected two day numbers`);
  }
  // a range that ends before it starts holds no days
  if (!(toDay >= fromDay)) {
    return [];
  }

  // a reading with a missing or broken number is left out rather than trusted
  const sorted = samples
    .filter(s => Number.isFinite(s.x) && Number.isFinite(s.y))
    .sort((a, b) => a.x - b.x);

  // the trailing fit ending on a given day; each is needed by up to windowDays days, so it is kept
  const fitCache = new Map<number, TrailingFit | undefined>();
  const trailingFit = (endDay: number): TrailingFit | undefined => {
    if (!fitCache.has(endDay)) {
      fitCache.set(endDay, fitWindow(sorted, endDay - windowDays + 1, endDay, options.minSamples));
    }
    return fitCache.get(endDay);
  };

  // the direction of a day compares its tunnel with the tunnel half a window earlier, so the
  // tunnel is worked out from that many days before the asked range. Where there is no earlier
  // tunnel (the first days of a series, or just after a long gap) it compares with the one half
  // a window later instead
  const lookDays = Math.round(windowDays / 2);
  const firstDay = fromDay - lookDays;
  const blended: (BlendedDay | undefined)[] = [];
  for (let day = firstDay; day <= toDay; day++) {
    blended.push(blendDay(day, toDay, windowDays, trailingFit));
  }
  const at = (day: number): BlendedDay | undefined => blended[day - firstDay];

  const result: (RunningTrendDay | undefined)[] = [];
  for (let day = fromDay; day <= toDay; day++) {
    const here = at(day);
    if (!here) {
      result.push(undefined);
      continue;
    }
    result.push({
      x: day,
      value: here.value,
      lower: here.lower,
      upper: here.upper,
      slope: here.slope,
      slope30: here.slope30,
      slope70: here.slope70,
      direction: directionOf(here, day, at(day - lookDays) ? day - lookDays : day + lookDays, at, options.flatPerWeek),
      // if a fit that would cover this day ends after today, it does not exist yet
      isProvisional: day + windowDays - 1 > toDay,
    });
  }
  return result;
}

/** One calendar day of a running trend, as a chart draws it. */
export interface RunningTrendRow {
  dateIso: string;
  /** undefined means a gap: nothing is drawn that day */
  value?: number;
  lower?: number;
  upper?: number;
  direction?: TrendDirection;
  isProvisional?: boolean;
}

/**
 * The days a caller must read readings for, so `buildRunningTrendRows` has every reading the range
 * needs: from 1.5 windows before the range (the first day's fits, and the tunnel half a window
 * earlier its direction is compared with) to `windowDays - 1` days after it, but never past
 * today. Pass the longest options a chart draws when one read serves several series.
 */
export function runningTrendReadWindow(
  range: { firstDayIso: string; lastDayIso: string; todayIso: string },
  options: RunningTrendOptions,
): { fromIso: string; toIso: string } {
  const windowDays = Math.floor(options.windowDays);
  const from = dayNumber(range.firstDayIso) - Math.ceil(windowDays * 1.5);
  const to = Math.min(dayNumber(range.lastDayIso) + windowDays - 1, dayNumber(range.todayIso));
  return { fromIso: dayIso(from), toIso: dayIso(to) };
}

/**
 * The running trend as one chart row per calendar day from `range.firstDayIso` to
 * `range.lastDayIso`, so a chart never repeats the day arithmetic.
 *
 * One reading per day is used, the last one given for that day. Values stay in the readings'
 * unit (kg, cm, mm), because the flat limit in `options` is in that unit; convert at display.
 *
 * The fits may reach up to `windowDays - 1` days past the range, but never past today, so a past
 * range is settled (no provisional days) and a day gets the same value whichever range it is
 * asked in. For that, the caller must hand in readings from at least 1.5 windows before the
 * range (21 days for weight, 42 for skinfolds and waist) up to `windowDays - 1` days after it;
 * this function uses what it is given. `todayIso` is the app's today (DebugService in the
 * client), and days after it come back as gaps.
 */
export function buildRunningTrendRows(
  readings: readonly { dateIso: string; value: number }[],
  range: { firstDayIso: string; lastDayIso: string; todayIso: string },
  options: RunningTrendOptions,
): RunningTrendRow[] {
  const firstDay = dayNumber(range.firstDayIso);
  const lastDay = dayNumber(range.lastDayIso);
  const today = dayNumber(range.todayIso);
  if (!(lastDay >= firstDay)) {
    return [];
  }
  const points = onePointPerDay(readings.map(r => ({ x: dayNumber(r.dateIso), y: r.value })));
  const toDay = Math.min(lastDay + Math.floor(options.windowDays) - 1, today);
  const trend = toDay >= firstDay ? computeRunningTrend(points, firstDay, toDay, options) : [];

  const rows: RunningTrendRow[] = [];
  for (let day = firstDay; day <= lastDay; day++) {
    const trendDay = trend[day - firstDay];
    rows.push(trendDay
      ? { dateIso: dayIso(day), value: trendDay.value, lower: trendDay.lower, upper: trendDay.upper, direction: trendDay.direction, isProvisional: trendDay.isProvisional }
      : { dateIso: dayIso(day) });
  }
  return rows;
}

interface BlendedDay {
  value: number;
  lower: number;
  upper: number;
  slope: number;
  slope30: number;
  slope70: number;
}

/** One straight Theil-Sen fit over the readings from firstDay to lastDay, both included. */
function fitWindow(sorted: { x: number; y: number }[], firstDay: number, lastDay: number, minSamples: number): TrailingFit | undefined {
  const points = sorted.filter(s => s.x >= firstDay && s.x <= lastDay);
  if (points.length < minSamples || points.length < 2) {
    return undefined;
  }
  const firstX = points[0].x;
  const lastX = points[points.length - 1].x;
  // if every reading is on the same day, there is no pair to take a slope from
  if (lastX - firstX < 1) {
    return undefined;
  }
  const slope = slopeEstimationPercentile(points, 0.5);
  const slope30 = slopeEstimationPercentile(points, 0.3);
  const slope70 = slopeEstimationPercentile(points, 0.7);
  const intercept = interceptEstimationPercentile(points, slope, 0.5);
  if (![slope, slope30, slope70, intercept].every(Number.isFinite)) {
    return undefined;
  }
  return { slope, intercept, slope30, slope70, firstX, lastX };
}

/**
 * The weighted average, for one day, of every trailing fit whose readings span that day.
 * `newestDay` is the last day a fit may end on.
 */
function blendDay(day: number, newestDay: number, windowDays: number, trailingFit: (endDay: number) => TrailingFit | undefined): BlendedDay | undefined {
  let weightSum = 0;
  let value = 0;
  let slope = 0;
  let slope30 = 0;
  let slope70 = 0;
  for (let endDay = day; endDay <= Math.min(day + windowDays - 1, newestDay); endDay++) {
    const fit = trailingFit(endDay);
    // if the day lies outside the readings this fit rests on, using it would extend the line past
    // its data, which draws values the user never had (#6015, #6050)
    if (!fit || day < fit.firstX || day > fit.lastX) {
      continue;
    }
    // a fit is most certain in the middle of its readings, so it counts most there
    const half = (fit.lastX - fit.firstX) / 2;
    const offCentre = Math.min(1, Math.abs(day - (fit.firstX + fit.lastX) / 2) / half);
    const weight = 1 - offCentre * offCentre + EDGE_WEIGHT;
    weightSum += weight;
    value += weight * (fit.slope * day + fit.intercept);
    slope += weight * fit.slope;
    slope30 += weight * fit.slope30;
    slope70 += weight * fit.slope70;
  }
  if (weightSum === 0) {
    return undefined;
  }
  value /= weightSum;
  slope /= weightSum;
  slope30 /= weightSum;
  slope70 /= weightSum;
  // the tunnel: where the trend would be half a window away if its slope were the 30th or the
  // 70th percentile slope instead of the median one
  const lever = windowDays / 2;
  return { value, slope, slope30, slope70, lower: value + (slope30 - slope) * lever, upper: value + (slope70 - slope) * lever };
}

/** Which way the tunnel is going at `day`, from where both its edges are against `otherDay`. */
function directionOf(here: BlendedDay, day: number, otherDay: number, at: (day: number) => BlendedDay | undefined, flatPerWeek: number): TrendDirection {
  const other = at(otherDay);
  if (!other) {
    return TrendDirection.Uncertain;
  }
  // change per week of each edge, signed so that later-minus-earlier is positive when rising
  const perWeek = 7 / (day - otherDay);
  const lowerChange = (here.lower - other.lower) * perWeek;
  const upperChange = (here.upper - other.upper) * perWeek;
  if (lowerChange < -flatPerWeek && upperChange < -flatPerWeek) {
    return TrendDirection.Falling;
  }
  if (lowerChange > flatPerWeek && upperChange > flatPerWeek) {
    return TrendDirection.Rising;
  }
  if (Math.abs(lowerChange) <= flatPerWeek && Math.abs(upperChange) <= flatPerWeek) {
    return TrendDirection.Flat;
  }
  return TrendDirection.Uncertain;
}
